import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { setSessionStorage, getSessionStorage } from "../../sessionStorage";
import type { ICourse } from "@/api/admin/course";

export interface IQualification {
  id?: string | number;
  name?: string;
  program_name?: string;
}

export interface ILeaddropFormData {
  mobileNumber: string;
  name: string;
  email: string;
  qualification: string;
  course: string;
  termsAccepted: boolean;
}

interface DefaultValues {
  phone?: string;
  name?: string;
  email?: string;
  qualification?: string;
  interest?: string;
  course?: string;
  media?: string;
  campaign?: string;
  termsAccepted?: boolean;
}

interface ILeaddropState {
  isOpen: boolean;
  variant: typeof LEAD_DROP_VARIANTS.REGISTER;
  type: typeof LEAD_DROP_VARIANTS.REGISTER;
  isExitFormOpen: boolean;
  isDelayFormOpen: boolean;
  downloadLink: string;
  formData: ILeaddropFormData | null;
  courses: ICourse[];
  defaultValues: DefaultValues;
  qualification: IQualification[];
  /** University Lead Popup section variant from settings (`"1"`, `"2"`, `"7"`, …). */
  leadDropPopupUiVariant: string | null;
  show_lead_drop: boolean;
  isPreview: boolean;
  mobileData: unknown;
  isMobile?: boolean;
  /** When the popup is opened from a Program Enrollments widget, the bound course (erp_unicode/id) is locked. */
  enrollmentBoundCourseSelectValue: string | null;
  lockEnrollmentCourse: boolean;
  /**
   * Set only by a referral CTA (Banner 1059). On a verified lead that the CRM
   * gave an id, the form sends them to the member area instead of the closing
   * card. Scoped to that one opener on purpose: every other popup on the site --
   * brochure, exit intent, timer -- must still end on its own success screen.
   */
  redirectToLaunchpadOnSuccess: boolean;
  /**
   * True only between an explicit "Download Brochure" click and the moment the
   * brochure is delivered. Gates the auto-download so it fires once per request
   * (after registration) and never re-fires from the persisted DOWNLOAD variant
   * on page navigation.
   */
  pendingBrochureDownload: boolean;
}

const initialState: ILeaddropState = {
  isOpen: false,
  variant: LEAD_DROP_VARIANTS.REGISTER,
  type: "",
  isExitFormOpen: false,
  isDelayFormOpen: false,
  downloadLink: "",
  formData: null,
  courses: [],
  defaultValues: {
    phone: "",
    name: "",
    email: "",
    qualification: "",
    interest: "",
    course: "",
    termsAccepted: false,
  },
  qualification: [],
  leadDropPopupUiVariant: null,
  show_lead_drop: false,
  isPreview: false,
  mobileData: null,
  isMobile: false,
  enrollmentBoundCourseSelectValue: null,
  lockEnrollmentCourse: false,
  redirectToLaunchpadOnSuccess: false,
  pendingBrochureDownload: false,
};

export const leaddropSlice = createSlice({
  name: "leaddrop",
  initialState,
  reducers: {
    handleOpenLeaddrop: (
      state,
      action: PayloadAction<{
        variant?:
          | typeof LEAD_DROP_VARIANTS.REGISTER
          | typeof LEAD_DROP_VARIANTS.DOWNLOAD;
        phoneNumber?: string;
        name?: string;
        email?: string;
        qualification?: string;
        course?: string;
        termsAccepted?: boolean;
        isExitFormOpen?: boolean;
        isDelayFormOpen?: boolean;
        downloadLink?: string;
        mobileData?: unknown;
        isMobile?: boolean;
        lockEnrollmentCourse?: boolean;
        redirectToLaunchpadOnSuccess?: boolean;
      }>,
    ) => {
      const isAutoTriggeredPopup =
        action.payload.isDelayFormOpen === true ||
        action.payload.isExitFormOpen === true;
      if (!state.show_lead_drop && isAutoTriggeredPopup) {
        return;
      }

      if (action.payload.mobileData) {
        state.mobileData = action.payload.mobileData;
      }
      if (action.payload.isMobile !== undefined) {
        state.isMobile = action.payload.isMobile;
      }

      state.isOpen = true;
      if (action.payload.variant) {
        state.variant = action.payload.variant;
        // Arm the brochure auto-download only for an explicit download request;
        // any other open (register / timer / exit) clears a stale pending flag.
        state.pendingBrochureDownload =
          action.payload.variant === LEAD_DROP_VARIANTS.DOWNLOAD;
      }
      if (action.payload.downloadLink) {
        state.downloadLink = action.payload.downloadLink;
      }
      /* Assigned on every open, not just when true: a referral CTA followed by
         a brochure click must not inherit the redirect. */
      state.redirectToLaunchpadOnSuccess =
        action.payload.redirectToLaunchpadOnSuccess === true;
      state.isExitFormOpen = action.payload.isExitFormOpen || false;
      state.isDelayFormOpen = action.payload.isDelayFormOpen || false;
      state.defaultValues = {
        phone: action.payload.phoneNumber || "",
        name: action.payload.name || "",
        email: action.payload.email || "",
        qualification: action.payload.qualification || "",
        interest: action.payload.course || "",
        course: action.payload.course || "",
        termsAccepted: action.payload.termsAccepted || false,
      };
      // When a Program Enrollments CTA passes an explicit lock, set it here.
      // Otherwise leave the page-level binding (set by setEnrollmentPageBinding)
      // untouched so timer / exit-intent / other CTAs still inherit it.
      if (
        action.payload.lockEnrollmentCourse === true &&
        String(action.payload.course ?? "").trim()
      ) {
        state.lockEnrollmentCourse = true;
        state.enrollmentBoundCourseSelectValue = String(
          action.payload.course ?? "",
        ).trim();
      }
    },
    handleCloseLeaddrop: (state) => {
      if (state.isExitFormOpen) {
        setSessionStorage(
          "show-on-exit",
          String(Number(getSessionStorage("show-on-exit") || "0") + 1),
        );
      }
      return {
        ...state,
        isOpen: false,
        isDelayFormOpen: false,
        isExitFormOpen: false,
        mobileData: null,
        isMobile: false,
        /*
         * HOW the popup was opened does not outlive the popup.
         *
         * A brochure CTA opens it with variant DOWNLOAD + a downloadLink, and nothing
         * cleared either on close. So a visitor who clicked "Download Brochure", closed
         * without registering, and later opened the form any other way — a Register CTA,
         * the delay timer, exit intent — landed on the brochure ending and was handed a
         * file they had not asked for on this open. `ConfigLeadForm` picks its ending
         * straight off this value (DOWNLOAD_VARIANT), and the legacy variant-7/8 thank-you
         * screens read `pendingBrochureDownload` the same way.
         *
         * Resetting costs nothing: every one of the call sites that opens for a download
         * passes BOTH the variant and the link on each click, so the next download open
         * re-declares them. REGISTER is also what a freshly loaded page starts on, so this
         * makes a close equivalent to a reload rather than a state no navigation produces.
         *
         * pendingBrochureDownload goes with them by its own definition — "true only
         * between an explicit Download Brochure click and the moment the brochure is
         * delivered". Closing the popup abandons the request; the delivery effects live
         * inside the dialog and are unmounted by then.
         */
        variant: LEAD_DROP_VARIANTS.REGISTER,
        downloadLink: "",
        pendingBrochureDownload: false,
        // NOTE: enrollmentBoundCourseSelectValue / lockEnrollmentCourse are
        // page-level (set by setEnrollmentPageBinding on the Enrollments widget)
        // and intentionally NOT reset here, so a timer/exit re-open still locks.
      };
    },
    handleSetLeaddropFormData: (
      state,
      action: PayloadAction<{
        formData: ILeaddropFormData;
        courses: ICourse[];
        qualification: IQualification[];
      }>,
    ) => {
      state.formData = action.payload.formData;
      state.courses = action.payload.courses;
      state.qualification = action.payload.qualification;
      // Also store in sessionStorage for persistence
      setSessionStorage("leaddrop-form-data", JSON.stringify(action.payload));
    },
    handleSetShowLeaddrop: (state, action: PayloadAction<boolean>) => {
      state.show_lead_drop = action.payload;
    },
    handleSetIsPreview: (state, action: PayloadAction<boolean>) => {
      state.isPreview = action.payload;
    },
    setLeadDropPopupUiVariant: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.leadDropPopupUiVariant = action.payload;
    },
    /**
     * Page-level program-enrollment binding. Dispatched by the Program
     * Enrollments widget on mount (and cleared on unmount) so EVERY popup-open
     * path on that page — timer, exit-intent, or any CTA — locks the lead form
     * to the bound course.
     */
    setEnrollmentPageBinding: (
      state,
      action: PayloadAction<{ course?: string | null; lock?: boolean }>,
    ) => {
      const course = String(action.payload.course ?? "").trim();
      const lock = action.payload.lock === true && Boolean(course);
      state.lockEnrollmentCourse = lock;
      state.enrollmentBoundCourseSelectValue = lock ? course : null;
    },
    /** Mark the pending brochure download as delivered so it won't re-fire. */
    consumeBrochureDownload: (state) => {
      state.pendingBrochureDownload = false;
    },
  },
});

export const {
  handleOpenLeaddrop,
  handleCloseLeaddrop,
  handleSetLeaddropFormData,
  handleSetShowLeaddrop,
  handleSetIsPreview,
  setLeadDropPopupUiVariant,
  setEnrollmentPageBinding,
  consumeBrochureDownload,
} = leaddropSlice.actions;
export default leaddropSlice.reducer;
